import { TextField } from '@mui/material';
import { createSupplierThunk, getSuppliersThunk } from '@redux/features/supplier.slice';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import FormModal from 'Common/Components/Modal/FormModal';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import { useFormik } from 'formik';
import React, { FC, useMemo } from 'react';
import * as Yup from 'yup';
import { Box, Button, Paper, Typography } from '@mui/material';
import GlnIdentityFields from 'components/integrations/GlnIdentityFields';

interface IProps {
    show: boolean;
    handleClose: () => void;
}

const AddSupplierModal: FC<IProps> = ({ show, handleClose }) => {
    const { loading } = useAppSelector((state: RootState) => (state as any).supplierState || { loading: false });
    const dispatch = useAppDispatch();

    const validationSchema = Yup.object({
        email: Yup.string().email('Invalid email').required('Email is required'),
        dunsNumber: Yup.string().required('DUNS Number is required'),
        accountType: Yup.string().required('Account Type is required'),
        businessName: Yup.string().required('Business name is required'),
        phoneNumber: Yup.string().required('Phone is required'),
        shippingAddressLine1: Yup.string().required('Shipping address line 1 is required'),
        shippingCity: Yup.string().required('Shipping city is required'),
        shippingState: Yup.string().required('Shipping state is required'),
        shippingZipCode: Yup.string().required('Shipping zip is required'),
        billingAddressLine1: Yup.string().required('Billing address line 1 is required'),
        billingCity: Yup.string().required('Billing city is required'),
        billingState: Yup.string().required('Billing state is required'),
        billingZipCode: Yup.string().required('Billing zip is required'),
        supplierGln: Yup.string()
            .optional()
            .matches(/^\d{13}$/, {
                message: 'Must be exactly 13 digits',
                excludeEmptyString: true,
            }),
    });

    const handleSubmit = (values: any) => {
        const body: any = {
            email: values.email,
            dunsNumber: values.dunsNumber,
            supplierGln: values.supplierGln?.trim() || undefined,
            supplierCompanyPrefix: values.supplierCompanyPrefix?.trim() || undefined,
            accountType: values.accountType,
            businessName: values.businessName,
            businessInfo: values.businessInfo,
            primaryContactName: values.primaryContactName,
            phoneNumber: values.phoneNumber,
            businessLicenceNumber: values.businessLicenceNumber,
            businessLicenceExpirationDate: values.businessLicenceExpirationDate,
            federalTaxID: values.federalTaxID,
            shippingAddressLine1: values.shippingAddressLine1,
            shippingAddressLine2: values.shippingAddressLine2,
            shippingCity: values.shippingCity,
            shippingState: values.shippingState,
            shippingZipCode: values.shippingZipCode,
            billingAddressLine1: values.billingAddressLine1,
            billingAddressLine2: values.billingAddressLine2,
            billingCity: values.billingCity,
            billingState: values.billingState,
            billingZipCode: values.billingZipCode,
            faxNumber: values.faxNumber,
            deaNumber: values.deaNumber,
            statePharmacyLicenseNumber: values.statePharmacyLicenseNumber,
            registrationDate: values.registrationDate,
            businessLicence: values.businessLicence,
            statePharmacyLicence: values.statePharmacyLicence,
            deaCertificate: values.deaCertificate,
            resaleCertificate: values.resaleCertificate,
        };
        dispatch(createSupplierThunk(body)).then((res: any) => {
            if (res.meta.requestStatus === 'fulfilled') {
                handleClose();
                formik.resetForm();
                dispatch(getSuppliersThunk());
            }
        });
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            email: '',
            dunsNumber: '',
            supplierGln: '',
            supplierCompanyPrefix: '',
            accountType: 'supplier',
            businessName: '',
            businessInfo: '',
            primaryContactName: '',
            phoneNumber: '',
            businessLicenceNumber: '',
            businessLicenceExpirationDate: '',
            federalTaxID: '',
            shippingAddressLine1: '',
            shippingAddressLine2: '',
            shippingCity: '',
            shippingState: '',
            shippingZipCode: '',
            billingAddressLine1: '',
            billingAddressLine2: '',
            billingCity: '',
            billingState: '',
            billingZipCode: '',
            faxNumber: '',
            deaNumber: '',
            statePharmacyLicenseNumber: '',
            registrationDate: '',
            businessLicence: undefined as unknown as File,
            statePharmacyLicence: undefined as unknown as File,
            deaCertificate: undefined as unknown as File,
            resaleCertificate: undefined as unknown as File,
        },
        validationSchema,
        onSubmit: handleSubmit,
    });

    const previews = useMemo(() => {
        const toUrl = (f?: File) => (f && f.type.startsWith('image/') ? URL.createObjectURL(f) : null);
        return {
            businessLicence: toUrl(formik.values.businessLicence),
            statePharmacyLicence: toUrl(formik.values.statePharmacyLicence),
            deaCertificate: toUrl(formik.values.deaCertificate),
            resaleCertificate: toUrl(formik.values.resaleCertificate),
        } as Record<string, string | null>;
    }, [
        formik.values.businessLicence,
        formik.values.statePharmacyLicence,
        formik.values.deaCertificate,
        formik.values.resaleCertificate,
    ]);

    return (
        <FormModal
            show={show}
            onHide={handleClose}
            title="Add Supplier"
            onSubmit={formik.handleSubmit}
            dialogClassName="relative z-[1050] mx-auto mt-10 w-full max-w-[900px]"
            footer={
                <>
                    <ButtonBase
                        innerText="Cancel"
                        className="bg-gray-500 hover:bg-gray-600 rounded border-none px-4 py-2 text-white"
                        onClick={() => {
                            handleClose();
                            formik.resetForm();
                        }}
                        disabled={loading}
                    />
                    <ButtonBase
                        innerText="Add Supplier"
                        className="bg-yellow-600 hover:bg-yellow-800 rounded border-none px-4 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={formik.handleSubmit}
                        disabled={
                            loading ||
                            !formik.values.email ||
                            !formik.values.dunsNumber ||
                            !formik.values.accountType ||
                            !formik.values.businessName
                        }
                    />
                </>
            }
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TextField
                        size="small"
                        fullWidth
                        id="email"
                        name="email"
                        label="Email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.email && Boolean(formik.errors.email)}
                        helperText={formik.touched.email && formik.errors.email}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="dunsNumber"
                        name="dunsNumber"
                        label="DUNS Number"
                        value={formik.values.dunsNumber}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.dunsNumber && Boolean(formik.errors.dunsNumber)}
                        helperText={formik.touched.dunsNumber && formik.errors.dunsNumber}
                    />
                    <Box className="col-span-full">
                        <GlnIdentityFields
                            mode="supplier"
                            disabled={loading}
                            values={{
                                gln: formik.values.supplierGln,
                                companyPrefix: formik.values.supplierCompanyPrefix,
                            }}
                            onChange={(field, value) => {
                                if (field === 'gln') {
                                    formik.setFieldValue('supplierGln', value);
                                } else if (field === 'companyPrefix') {
                                    formik.setFieldValue('supplierCompanyPrefix', value);
                                }
                            }}
                        />
                    </Box>
                    <TextField
                        size="small"
                        fullWidth
                        id="accountType"
                        name="accountType"
                        label="Account Type"
                        value={formik.values.accountType}
                        onChange={formik.handleChange}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="businessName"
                        name="businessName"
                        label="Business Name"
                        value={formik.values.businessName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.businessName && Boolean(formik.errors.businessName)}
                        helperText={formik.touched.businessName && formik.errors.businessName}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="businessInfo"
                        name="businessInfo"
                        label="Business Info"
                        value={formik.values.businessInfo}
                        onChange={formik.handleChange}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="primaryContactName"
                        name="primaryContactName"
                        label="Primary Contact Name"
                        value={formik.values.primaryContactName}
                        onChange={formik.handleChange}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="phoneNumber"
                        name="phoneNumber"
                        label="Phone Number"
                        value={formik.values.phoneNumber}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
                        helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="businessLicenceNumber"
                        name="businessLicenceNumber"
                        label="Business Licence Number"
                        value={formik.values.businessLicenceNumber}
                        onChange={formik.handleChange}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="businessLicenceExpirationDate"
                        name="businessLicenceExpirationDate"
                        label="Business Licence Expiration Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={formik.values.businessLicenceExpirationDate}
                        onChange={formik.handleChange}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="federalTaxID"
                        name="federalTaxID"
                        label="Federal Tax ID"
                        value={formik.values.federalTaxID}
                        onChange={formik.handleChange}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="shippingAddressLine1"
                        name="shippingAddressLine1"
                        label="Shipping Address Line 1"
                        value={formik.values.shippingAddressLine1}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.shippingAddressLine1 && Boolean(formik.errors.shippingAddressLine1)}
                        helperText={formik.touched.shippingAddressLine1 && formik.errors.shippingAddressLine1}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="shippingAddressLine2"
                        name="shippingAddressLine2"
                        label="Shipping Address Line 2"
                        value={formik.values.shippingAddressLine2}
                        onChange={formik.handleChange}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="shippingCity"
                        name="shippingCity"
                        label="Shipping City"
                        value={formik.values.shippingCity}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.shippingCity && Boolean(formik.errors.shippingCity)}
                        helperText={formik.touched.shippingCity && formik.errors.shippingCity}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="shippingState"
                        name="shippingState"
                        label="Shipping State"
                        value={formik.values.shippingState}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.shippingState && Boolean(formik.errors.shippingState)}
                        helperText={formik.touched.shippingState && formik.errors.shippingState}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="shippingZipCode"
                        name="shippingZipCode"
                        label="Shipping Zip Code"
                        value={formik.values.shippingZipCode}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.shippingZipCode && Boolean(formik.errors.shippingZipCode)}
                        helperText={formik.touched.shippingZipCode && formik.errors.shippingZipCode}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="billingAddressLine1"
                        name="billingAddressLine1"
                        label="Billing Address Line 1"
                        value={formik.values.billingAddressLine1}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.billingAddressLine1 && Boolean(formik.errors.billingAddressLine1)}
                        helperText={formik.touched.billingAddressLine1 && formik.errors.billingAddressLine1}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="billingAddressLine2"
                        name="billingAddressLine2"
                        label="Billing Address Line 2"
                        value={formik.values.billingAddressLine2}
                        onChange={formik.handleChange}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="billingCity"
                        name="billingCity"
                        label="Billing City"
                        value={formik.values.billingCity}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.billingCity && Boolean(formik.errors.billingCity)}
                        helperText={formik.touched.billingCity && formik.errors.billingCity}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="billingState"
                        name="billingState"
                        label="Billing State"
                        value={formik.values.billingState}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.billingState && Boolean(formik.errors.billingState)}
                        helperText={formik.touched.billingState && formik.errors.billingState}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="billingZipCode"
                        name="billingZipCode"
                        label="Billing Zip Code"
                        value={formik.values.billingZipCode}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.billingZipCode && Boolean(formik.errors.billingZipCode)}
                        helperText={formik.touched.billingZipCode && formik.errors.billingZipCode}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="faxNumber"
                        name="faxNumber"
                        label="Fax Number"
                        value={formik.values.faxNumber}
                        onChange={formik.handleChange}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="deaNumber"
                        name="deaNumber"
                        label="DEA Number"
                        value={formik.values.deaNumber}
                        onChange={formik.handleChange}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="statePharmacyLicenseNumber"
                        name="statePharmacyLicenseNumber"
                        label="State Pharmacy License Number"
                        value={formik.values.statePharmacyLicenseNumber}
                        onChange={formik.handleChange}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="registrationDate"
                        name="registrationDate"
                        label="Registration Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={formik.values.registrationDate}
                        onChange={formik.handleChange}
                    />

                    <div className="col-span-full grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[
                            { key: 'businessLicence', label: 'Business Licence (file)' },
                            { key: 'statePharmacyLicence', label: 'State Pharmacy Licence (file)' },
                            { key: 'deaCertificate', label: 'DEA Certificate (file)' },
                            { key: 'resaleCertificate', label: 'Resale Certificate (file)' },
                        ].map((f) => {
                            const file = (formik.values as any)[f.key] as File | undefined;
                            const preview = (previews as any)[f.key] as string | null;
                            return (
                                <Paper key={f.key} variant="outlined" className="p-3 flex items-center gap-3">
                                    <Box className="flex-1">
                                        <Typography variant="body2" className="mb-1 text-slate-700">
                                            {f.label}
                                        </Typography>
                                        {file ? (
                                            <Box className="flex items-center gap-3">
                                                {preview ? (
                                                    // eslint-disable-next-line @next/next/no-img-element
                                                    <img
                                                        src={preview}
                                                        alt="preview"
                                                        className="w-12 h-12 rounded object-cover border"
                                                    />
                                                ) : (
                                                    <Box className="w-12 h-12 rounded border flex items-center justify-center text-xs text-slate-500">
                                                        File
                                                    </Box>
                                                )}
                                                <Box className="min-w-0">
                                                    <Typography variant="body2" className="truncate max-w-[220px]">
                                                        {file.name}
                                                    </Typography>
                                                    <Typography variant="caption" className="text-slate-500">
                                                        {(file.size / 1024).toFixed(1)} KB
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ) : (
                                            <Typography variant="caption" className="text-slate-500">
                                                No file selected
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box className="flex items-center gap-2">
                                        <Button variant="outlined" size="small" component="label">
                                            Upload
                                            <input
                                                hidden
                                                type="file"
                                                accept="image/*,.pdf,.doc,.docx,.png,.jpg,.jpeg"
                                                onChange={(e) =>
                                                    (formik as any).setFieldValue(f.key, e.currentTarget.files?.[0])
                                                }
                                            />
                                        </Button>
                                        {file && (
                                            <Button
                                                variant="text"
                                                size="small"
                                                onClick={() => (formik as any).setFieldValue(f.key, undefined)}
                                            >
                                                Remove
                                            </Button>
                                        )}
                                    </Box>
                                </Paper>
                            );
                        })}
                    </div>
            </div>
        </FormModal>
    );
};

export default AddSupplierModal;
